/**
 * Compute Total Carried Forward using brought-forward + sector totals rules.
 * Used for "Calculate Total Carried Forward" in Technical Log Book entry form.
 */

const BUILT_IN_TOTALS_KEYS = [
  'airframeHours',
  'landings',
  'engine1Hours',
  'engine2Hours',
  'lhEngineCyclesCT',
  'lhEngineCyclesPT',
  'lhEngineCyclesIMP',
  'rhEngineCyclesCT',
  'rhEngineCyclesPT',
  'rhEngineCyclesIMP',
  'lgLeftOvspdCy',
  'lgRightOvspdCy',
  'nrAndHydraulicCycles',
  'lgNoseOvspdCy',
  'lgExtRecCyc',
  'rotorBrakeRbcy',
  'mgbTorqueTcy',
  'diskWear',
  'flightAirTime',
  'totalAirTime',
  'blockTime',
  'totalLandings',
  'fuelConsumption',
  'rhEngineOilConsumption',
  'lhEngineOilConsumption',
  'hobbs',
  'hobbsAC'
];

const HOBBS_METER_KEYS = new Set(['hobbs', 'hobbsAC']);

const HOURS_KEYS = new Set(['airframeHours', 'engine1Hours', 'engine2Hours', 'flightAirTime', 'totalAirTime']);
const CARRIED_FORWARD_ONLY_KEYS = new Set(['flightAirTime', 'totalAirTime', 'blockTime', 'totalLandings']);
const LAST_SECTOR_ENGINE_CYCLE_KEYS = new Set([
  'lhEngineCyclesCT',
  'lhEngineCyclesPT',
  'lhEngineCyclesIMP',
  'rhEngineCyclesCT',
  'rhEngineCyclesPT',
  'rhEngineCyclesIMP'
]);

/**
 * Normalize totals numbers to at most two fractional digits.
 * - If the value is already on the 0.01 grid (including float noise), keep that hundredth — do not round away.
 * - If the fixed-point string shows at most two fractional digits (after trimming trailing zeros), keep that
 *   literal value (no Math.round to a different hundredth).
 * - Otherwise round to the nearest hundredth (e.g. 552.691 → 552.69).
 * @param {string} _key - Totals object key (reserved for per-key rules)
 * @param {unknown} value - Raw numeric
 * @returns {number}
 */
export function roundTotalsNumeric(_key, value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n === 0) return 0;

  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const atCent = Math.round(n * 100) / 100;
  const onCentGrid = Math.abs(n - atCent) < 1e-5;

  const fixed = abs.toFixed(10);
  const dot = fixed.indexOf('.');
  const whole = dot === -1 ? fixed : fixed.slice(0, dot);
  const fracRaw = dot === -1 ? '' : fixed.slice(dot + 1);
  const fracTrimmed = fracRaw.replace(/0+$/, '');

  if (onCentGrid) {
    return atCent;
  }

  if (fracTrimmed.length <= 2) {
    const s = fracTrimmed.length ? `${whole}.${fracTrimmed}` : whole;
    return sign * parseFloat(s);
  }

  return atCent;
}

function parseHHMMToHours(raw) {
  if (raw == null) return 0;
  const text = String(raw).trim();
  if (!text) return 0;
  if (!/^\d{3,4}$/.test(text)) return 0;
  const padded = text.padStart(4, '0');
  const hours = Number(padded.slice(0, 2));
  const minutes = Number(padded.slice(2, 4));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes >= 60) return 0;
  return hours + minutes / 60;
}
/**
 * Parse "H:MM" or "H:MM:SS" string to decimal hours.
 * @param {string} s - Time string
 * @returns {number} Decimal hours, or 0 if invalid
 */
export function parseHMMToHours(s) {
  if (!s || typeof s !== 'string') return 0;
  const trimmed = s.trim();
  if (!trimmed) return 0;
  if (!trimmed.includes(':')) {
    const decimal = Number(trimmed.replace(',', '.'));
    return Number.isFinite(decimal) ? decimal : 0;
  }
  const [hRaw = '0', mRaw = '0', secRaw = '0'] = trimmed.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  const sec = Number(secRaw);
  if (![h, m, sec].every((n) => Number.isFinite(n))) return 0;
  return h + m / 60 + sec / 3600;
}

/**
 * Get numeric value for a key from a sector (including flight time for hours keys).
 * @param {object} sector - Sector data (sector1, sector2, ...)
 * @param {string} key - Field key
 * @returns {number}
 */
function getSectorNumericValue(sector, key) {
  if (!sector) return 0;
  if (HOURS_KEYS.has(key)) {
    // Always use airborne time for hours totals; keep typo-compatible fallback.
    return parseHMMToHours(sector.airboneTime ?? sector.airborneTime);
  }
  if (key === 'blockTime') {
    // Sector block time is stored as HHMM (e.g. "0125"), convert to decimal hours before summing.
    return parseHHMMToHours(sector.blockTime);
  }
  if (key === 'landings') {
    const n = sector.landings;
    return typeof n === 'number' && !isNaN(n) ? n : 0;
  }
  const fromCustom = sector.customFields && sector.customFields[key];
  if (fromCustom !== undefined && fromCustom !== null && fromCustom !== '') {
    const n = Number(fromCustom);
    return isNaN(n) ? 0 : n;
  }
  const fromRoot = sector[key];
  if (fromRoot !== undefined && fromRoot !== null && fromRoot !== '') {
    const n = Number(fromRoot);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function sectorHasEnteredValue(sector, key) {
  if (!sector) return false;
  const fromCustom = sector.customFields && sector.customFields[key];
  if (fromCustom !== undefined && fromCustom !== null && fromCustom !== '') return true;
  const fromRoot = sector[key];
  return fromRoot !== undefined && fromRoot !== null && fromRoot !== '';
}

/**
 * Collect all keys that should be computed: built-in + totalBroughtForward + sector customFields + optional (e.g. Totals custom field keys).
 * @param {object} formData - Full form data
 * @param {string[]} [optionalKeys] - Extra keys to include (e.g. from format Totals custom fields, so we always compute them)
 * @returns {string[]} Unique keys
 */
function getAllTotalsKeys(formData, optionalKeys = []) {
  const keys = new Set(BUILT_IN_TOTALS_KEYS);
  const tbf = formData?.totalBroughtForward;
  if (tbf && typeof tbf === 'object') {
    Object.keys(tbf).forEach((k) => keys.add(k));
  }
  for (let i = 1; i <= 5; i++) {
    const sector = formData?.[`sector${i}`];
    const cf = sector?.customFields;
    if (cf && typeof cf === 'object') {
      Object.keys(cf).forEach((k) => keys.add(k));
    }
  }
  (optionalKeys || []).forEach((k) => keys.add(k));
  return Array.from(keys);
}

/**
 * Compute totalCarriedForward from brought-forward + sector totals.
 * Engine cycle keys are calculated using only the last available sector and
 * do not include brought-forward values.
 * @param {object} formData - Current form data (totalBroughtForward, sector1..sector5)
 * @param {string[]} [optionalKeys] - Extra keys to compute (e.g. base keys from format Totals custom fields)
 * @returns {object} New totalCarriedForward object (only numeric keys; values are calculated)
 */
export function computeTotalCarriedForward(formData, optionalKeys = []) {
  const tbf = formData?.totalBroughtForward || {};
  const result = {};

  const keys = getAllTotalsKeys(formData, optionalKeys);

  for (const key of keys) {
    let brought = (CARRIED_FORWARD_ONLY_KEYS.has(key) || LAST_SECTOR_ENGINE_CYCLE_KEYS.has(key)) ? 0 : tbf[key];
    if (brought !== undefined && brought !== null && brought !== '') {
      brought = Number(brought);
    } else {
      brought = 0;
    }
    if (isNaN(brought)) brought = 0;

    if (HOBBS_METER_KEYS.has(key)) {
      let reading = null;
      for (let i = 5; i >= 1; i--) {
        const sector = formData?.[`sector${i}`];
        if (!sectorHasEnteredValue(sector, key)) continue;
        reading = getSectorNumericValue(sector, key);
        break;
      }
      result[key] = roundTotalsNumeric(key, reading !== null ? reading : brought);
      continue;
    }

    let sectorContribution = 0;
    if (LAST_SECTOR_ENGINE_CYCLE_KEYS.has(key)) {
      for (let i = 5; i >= 1; i--) {
        const sector = formData?.[`sector${i}`];
        if (!sector || typeof sector !== 'object') continue;
        sectorContribution = getSectorNumericValue(sector, key);
        break;
      }
    } else {
      for (let i = 1; i <= 5; i++) {
        const sector = formData?.[`sector${i}`];
        if (!sector || typeof sector !== 'object') continue;
        sectorContribution += getSectorNumericValue(sector, key);
      }
    }

    result[key] = roundTotalsNumeric(key, brought + sectorContribution);
  }

  return result;
}
